"use client";

import { c as _c } from "react/compiler-runtime";
import type { Route } from "next";
import NextLink from "next/link";
import type { ComponentProps } from "react";
import { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { UrlObject } from "url";
import { TRACKING_PARAMS } from "@local/tracking/src/acceptableMktParams";
import { useHydrated } from "@local/utils/src/useHydrated";
type NextLinkComponentProps = ComponentProps<typeof NextLink>;
export type LinkProps = Omit<NextLinkComponentProps, "href"> & {
  href: string | UrlObject;
  preserveParams?: boolean;
};
const INTERNAL_RE = /^\/(?!\/)/;
const PREFETCH_DELAY = 500;
const hrefToString = (href: string | UrlObject): string | null => {
  if (typeof href === "string") return href;
  if (!href.pathname) return null;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(href.query ?? {})) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  const hash = href.hash ? href.hash.startsWith("#") ? href.hash : `#${href.hash}` : "";
  return `${href.pathname}${query ? `?${query}` : ""}${hash}`;
};
const mergeTrackingParams = (hrefStr: string, currentParams: URLSearchParams): string => {
  const hashIdx = hrefStr.indexOf("#");
  const hash = hashIdx >= 0 ? hrefStr.slice(hashIdx) : "";
  const withoutHash = hashIdx >= 0 ? hrefStr.slice(0, hashIdx) : hrefStr;
  const qIdx = withoutHash.indexOf("?");
  const pathname = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const target = new URLSearchParams(qIdx >= 0 ? withoutHash.slice(qIdx + 1) : "");
  for (const [key, value] of currentParams.entries()) {
    if (TRACKING_PARAMS.has(key) && !target.has(key)) {
      target.set(key, value);
    }
  }
  const qs = target.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
};
const useResolvedHref = (href, preserveParams) => {
  const $ = _c(5);
  const currentParams = useSearchParams();
  const isHydrated = useHydrated();
  let t0;
  if ($[0] !== currentParams || $[1] !== href || $[2] !== isHydrated || $[3] !== preserveParams) {
    bb0: {
      if (!isHydrated || !preserveParams) {
        t0 = href;
        break bb0;
      }
      const str = hrefToString(href);
      if (!str || !INTERNAL_RE.test(str)) {
        t0 = href;
        break bb0;
      }
      if (currentParams.size === 0) {
        t0 = href;
        break bb0;
      }
      t0 = mergeTrackingParams(str, currentParams) as Route;
    }
    $[0] = currentParams;
    $[1] = href;
    $[2] = isHydrated;
    $[3] = preserveParams;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
};
const LinkInner = t0 => {
  const $ = _c(43);
  let href;
  let onFocus;
  let onMouseEnter;
  let onMouseLeave;
  let onTouchStart;
  let prefetch;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      prefetch,
      onMouseEnter,
      onMouseLeave,
      onTouchStart,
      onFocus,
      href,
      preserveParams: t1,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = href;
    $[2] = onFocus;
    $[3] = onMouseEnter;
    $[4] = onMouseLeave;
    $[5] = onTouchStart;
    $[6] = prefetch;
    $[7] = rest;
    $[8] = t1;
  } else {
    href = $[1];
    onFocus = $[2];
    onMouseEnter = $[3];
    onMouseLeave = $[4];
    onTouchStart = $[5];
    prefetch = $[6];
    rest = $[7];
    t1 = $[8];
  }
  const preserveParams = t1 === undefined ? true : t1;
  const router = useRouter();
  const resolvedHref = useResolvedHref(href, preserveParams);
  const typedHref = resolvedHref as Route;
  const hasPrefetched = useRef(false);
  const hoverTimer = useRef(undefined);
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      hasPrefetched.current = false;
    };
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== href) {
    t3 = [href];
    $[10] = href;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => clearTimeout(hoverTimer.current);
    t5 = [];
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== resolvedHref || $[15] !== router) {
    t6 = () => {
      if (hasPrefetched.current) {
        return;
      }
      const url = hrefToString(resolvedHref);
      if (!url || !INTERNAL_RE.test(url)) {
        return;
      }
      hasPrefetched.current = true;
      router.prefetch(url as Route);
    };
    $[14] = resolvedHref;
    $[15] = router;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const prefetchOnIntent = t6;
  if (prefetch !== false) {
    let t7;
    if ($[17] !== onFocus || $[18] !== onMouseEnter || $[19] !== onMouseLeave || $[20] !== onTouchStart || $[21] !== prefetch || $[22] !== rest || $[23] !== typedHref) {
      t7 = <NextLink href={typedHref} prefetch={prefetch} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onTouchStart={onTouchStart} onFocus={onFocus} {...rest} />;
      $[17] = onFocus;
      $[18] = onMouseEnter;
      $[19] = onMouseLeave;
      $[20] = onTouchStart;
      $[21] = prefetch;
      $[22] = rest;
      $[23] = typedHref;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  let t7;
  if ($[25] !== onMouseEnter || $[26] !== prefetchOnIntent) {
    t7 = e => {
      hoverTimer.current = setTimeout(prefetchOnIntent, PREFETCH_DELAY);
      onMouseEnter?.(e);
    };
    $[25] = onMouseEnter;
    $[26] = prefetchOnIntent;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== onMouseLeave) {
    t8 = e_0 => {
      clearTimeout(hoverTimer.current);
      onMouseLeave?.(e_0);
    };
    $[28] = onMouseLeave;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== onTouchStart || $[31] !== prefetchOnIntent) {
    t9 = e_1 => {
      prefetchOnIntent();
      onTouchStart?.(e_1);
    };
    $[30] = onTouchStart;
    $[31] = prefetchOnIntent;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== onFocus || $[34] !== prefetchOnIntent) {
    t10 = e_2 => {
      prefetchOnIntent();
      onFocus?.(e_2);
    };
    $[33] = onFocus;
    $[34] = prefetchOnIntent;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== rest || $[37] !== t10 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9 || $[41] !== typedHref) {
    t11 = <NextLink href={typedHref} prefetch={false} onMouseEnter={t7} onMouseLeave={t8} onTouchStart={t9} onFocus={t10} {...rest} />;
    $[36] = rest;
    $[37] = t10;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = typedHref;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  return t11;
};
export const Link = props => {
  const $ = _c(9);
  const t0 = props.href as Route;
  const t1 = props.prefetch ?? false;
  let t2;
  if ($[0] !== props || $[1] !== t0 || $[2] !== t1) {
    t2 = <NextLink {...props} href={t0} prefetch={t1} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props) {
    t3 = <LinkInner {...props} />;
    $[4] = props;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <Suspense fallback={t2}>{t3}</Suspense>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};